import { useState, useEffect } from 'react';
import './FeedList.css';
import FeedElement from './FeedElement';
import UserDetail from './UserDetail';
import { feedAPI } from './services/api';

function FeedList({ currentUser, refreshTrigger }) {
  const [feedItems, setFeedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  useEffect(() => {
    if (!currentUser) {
      setFeedItems([]);
      setLoading(false);
      return;
    }

    const loadFeed = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await feedAPI.getUserFeed(currentUser.id);
        setFeedItems(data);
      } catch (err) {
        console.error('Error loading feed:', err);
        setError('Failed to load feed. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadFeed();
  }, [currentUser, refreshTrigger]);

  const handleAvatarClick = (userId) => {
    setSelectedUserId(userId);
  };

  const handleCloseUserDetail = () => {
    setSelectedUserId(null);
  };

  if (loading) {
    return (
      <div className="feed-list">
        <div className="feed-loading">Loading feed...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="feed-list">
        <div className="feed-error">{error}</div>
      </div>
    );
  }

  if (feedItems.length === 0) {
    return (
      <div className="feed-list">
        <div className="feed-empty">
          <p>No feed items yet.</p>
          <p className="feed-empty-hint">
            Your friends' posts and activities will appear here!
          </p>
        </div>
      </div>
    );
  }

  // Group feed items by eventId (if they have one)
  const groupedItems = [];
  const eventGroups = new Map();

  feedItems.forEach(item => {
    // Group multiple users attending the same event
    // Only group event attendance items (eventId present, no picture/link/badge)
    if (item.eventId && !item.pictureId && !item.externalLink && !item.badgeId) {
      if (!eventGroups.has(item.eventId)) {
        eventGroups.set(item.eventId, {
          eventId: item.eventId,
          eventName: item.eventName,
          eventDateTime: item.eventDateTime, // Include the actual event date/time
          eventPictureSysName: item.eventPictureSysName, // Event picture if organizer added one
          eventOrganizerUsername: item.eventOrganizerUsername, // Username for picture path
          eventCommunityEventOrganizerUsername: item.eventCommunityEventOrganizerUsername, // Username of community event organizer (when picture comes from community event)
          createdAt: item.createdAt, // Use the earliest createdAt
          users: []
        });
      }
      const group = eventGroups.get(item.eventId);

      // Only add user if they're not already in the group (avoid duplicates)
      const userAlreadyInGroup = group.users.some(u => u.userId === item.userId);
      if (!userAlreadyInGroup) {
        group.users.push({
          userId: item.userId,
          displayName: item.userDisplayName
        });
      }

      // Keep track of the earliest post time
      if (new Date(item.createdAt) < new Date(group.createdAt)) {
        group.createdAt = item.createdAt;
      }
    } else {
      // Non-event items or items with pictures/links/badges remain separate
      groupedItems.push({
        type: 'single',
        item: item
      });
    }
  });

  // Add grouped event items
  eventGroups.forEach(group => {
    groupedItems.push({
      type: 'group',
      group: group
    });
  });

  // Sort by createdAt (most recent first)
  groupedItems.sort((a, b) => {
    const dateA = a.type === 'single' ? new Date(a.item.createdAt) : new Date(a.group.createdAt);
    const dateB = b.type === 'single' ? new Date(b.item.createdAt) : new Date(b.group.createdAt);
    return dateB - dateA;
  });

  return (
    <div className="feed-list">
      <h2 className="feed-list-title">Your Feed</h2>
      {groupedItems.map((item, index) => {
        if (item.type === 'single') {
          return (
            <FeedElement
              key={item.item.id}
              feedItem={item.item}
              currentUser={currentUser}
              onAvatarClick={handleAvatarClick}
            />
          );
        } else {
          // Render grouped event item
          return (
            <FeedElement
              key={`group-${item.group.eventId}-${index}`}
              feedItem={{
                id: `group-${item.group.eventId}`,
                eventId: item.group.eventId,
                eventName: item.group.eventName,
                eventDateTime: item.group.eventDateTime,
                eventPictureSysName: item.group.eventPictureSysName,
                eventOrganizerUsername: item.group.eventOrganizerUsername,
                eventCommunityEventOrganizerUsername: item.group.eventCommunityEventOrganizerUsername,
                createdAt: item.group.createdAt,
                groupedUsers: item.group.users
              }}
              currentUser={currentUser}
              onAvatarClick={handleAvatarClick}
            />
          );
        }
      })}

      {selectedUserId && (
        <UserDetail
          userId={selectedUserId}
          onClose={handleCloseUserDetail}
        />
      )}
    </div>
  );
}

export default FeedList;
